/** @format — Date filter dropdown with custom range picker */

import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import FilterDropdown from "@/components/filters/FilterDropdown";
import FilterMenuItem from "@/components/filters/FilterMenuItem";
import FilterMenuPanel from "@/components/filters/FilterMenuPanel";
import CompactCalendar from "./CompactCalendar";
import { cn } from "@/lib/utils";
import {
  DATE_FILTER_OPTIONS,
  formatCustomDateRangeLabel,
} from "../utils/documentFormatters";
import type { CustomDateRange, DocumentDateFilter } from "../types";

function formatInputDate(date?: Date) {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const DATE_MENU_ITEM_CLASS = "px-3 py-2 md:px-2 md:py-[5.5px]";

export default function DocumentsDateFilterDropdown({
  value,
  customRange,
  onChange,
  onCustomRangeChange,
  buttonClassName,
  compactLabel,
}: {
  value: DocumentDateFilter | string;
  customRange: CustomDateRange;
  onChange: (value: DocumentDateFilter | string) => void;
  onCustomRangeChange: (range: CustomDateRange) => void;
  buttonClassName?: string;
  compactLabel?: string;
}) {
  const [draftRange, setDraftRange] = useState<CustomDateRange>(customRange);
  const [activeField, setActiveField] = useState<"from" | "to" | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    customRange.from ?? customRange.to ?? new Date(),
  );

  const isActive = value !== "all";
  const label =
    value === "custom"
      ? formatCustomDateRangeLabel(customRange)
      : DATE_FILTER_OPTIONS.find((option) => option.value === value)?.label ?? "Date";

  const selectedCalendarDate =
    activeField === "from" ? draftRange.from : activeField === "to" ? draftRange.to : undefined;

  const showCalendar = value === "custom" && activeField !== null;

  const handleDateSelect = (date: Date) => {
    if (!activeField) return;
    setDraftRange((current) => ({
      ...current,
      [activeField]: date,
    }));
    setCalendarMonth(date);
  };

  const desktopCalendar = showCalendar ? (
    <CompactCalendar
      month={calendarMonth}
      onMonthChange={setCalendarMonth}
      selected={selectedCalendarDate}
      onSelect={handleDateSelect}
    />
  ) : null;

  const mobileCalendar = showCalendar ? (
    <CompactCalendar
      embedded
      month={calendarMonth}
      onMonthChange={setCalendarMonth}
      selected={selectedCalendarDate}
      onSelect={handleDateSelect}
    />
  ) : null;

  return (
    <FilterDropdown
      label="Date"
      valueLabel={label}
      compactLabel={compactLabel ?? (value === "all" ? "Date" : label)}
      active={isActive}
      buttonClassName={buttonClassName}
      mobileCenter
    >
      {(close) => (
        <div
          className={cn(
            "max-w-[calc(100vw-16px)] overflow-hidden p-0.5 md:max-w-none",
            showCalendar ? "flex items-end gap-2" : "",
          )}
        >
          {showCalendar ? (
            <div className="hidden shrink-0 md:block">{desktopCalendar}</div>
          ) : null}

          <FilterMenuPanel className="w-[min(100vw-16px,266px)] min-w-0 p-1 md:w-[266px] md:p-0.5">
            {DATE_FILTER_OPTIONS.map((option) => (
              <FilterMenuItem
                key={option.value}
                active={value === option.value}
                className={DATE_MENU_ITEM_CLASS}
                onClick={() => {
                  onChange(option.value);
                  if (option.value === "custom") {
                    setDraftRange(customRange);
                    setCalendarMonth(customRange.from ?? customRange.to ?? new Date());
                    setActiveField(null);
                  } else {
                    setActiveField(null);
                    close();
                  }
                }}
              >
                {option.label}
              </FilterMenuItem>
            ))}

            {value === "custom" ? (
              <div className="flex flex-col gap-2 px-2 py-2 md:p-2">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-[#0a0a0a]">From</p>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveField("from");
                      setCalendarMonth(draftRange.from ?? draftRange.to ?? new Date());
                    }}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg border px-2 py-[5.5px] text-sm text-[#0a0a0a]",
                      activeField === "from"
                        ? "border-blue-800 bg-blue-50 shadow-[0px_0px_0px_2px_rgba(37,99,235,0.8)]"
                        : "border-neutral-200 bg-white",
                    )}
                  >
                    <CalendarIcon className="h-4 w-4 shrink-0 text-[#737373]" />
                    <span className="truncate">{formatInputDate(draftRange.from) || "Select date"}</span>
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-[#0a0a0a]">To</p>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveField("to");
                      setCalendarMonth(draftRange.to ?? draftRange.from ?? new Date());
                    }}
                    className={cn(
                      "flex items-center justify-between gap-1.5 rounded-lg border px-2 py-[5.5px] text-sm text-[#0a0a0a]",
                      activeField === "to"
                        ? "border-blue-800 bg-blue-50 shadow-[0px_0px_0px_2px_rgba(37,99,235,0.8)]"
                        : "border-neutral-200 bg-white",
                    )}
                  >
                    <span className="truncate">{formatInputDate(draftRange.to) || "Select date"}</span>
                    <CalendarIcon className="h-4 w-4 shrink-0 text-[#737373]" />
                  </button>
                </div>

                {showCalendar ? (
                  <div className="w-full min-w-0 overflow-hidden pt-1 md:hidden">{mobileCalendar}</div>
                ) : null}

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    className="flex-1 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-[#0a0a0a]"
                    onClick={() => {
                      setDraftRange({});
                      setActiveField(null);
                      onCustomRangeChange({});
                      onChange("all");
                      close();
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-neutral-50"
                    onClick={() => {
                      onCustomRangeChange(draftRange);
                      setActiveField(null);
                      close();
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            ) : null}
          </FilterMenuPanel>
        </div>
      )}
    </FilterDropdown>
  );
}
